/**
 * A comment exchange as the session's own transcript shows it (RFC 0324 3d).
 *
 * The comment that woke the session is a CARD: who wrote it, the passage, the
 * words, and a link back, because it is what the agent answers. What the agent
 * then wrote in a thread is a ROW, one line naming whose comment it answered
 * and where, so the transcript still reads as the agent's work and not as a
 * second copy of the thread.
 */

import type { CommentAuthor, TranscriptMessage } from '@mod/core/model'
import { MessageSquare } from 'lucide-react'
import type { ThreadCard } from './threadCards'

/** What the project knows about a comment, looked up by id. The event only
 *  carries the first line of the body; the project has the rest. */
export interface CommentFacts {
  author: CommentAuthor
  body?: string
  createdAt?: number
  documentId?: string
}

/** Resolve a comment by id, or failing that its writer by the stored author. */
export type CommentLookup = (
  commentId: string | undefined,
  author?: string,
) => CommentFacts | undefined

function ago(at: number | undefined, now = Date.now()): string {
  if (!at) return ''
  const m = Math.max(0, Math.round((now - at) / 60_000))
  if (m < 1) return 'now'
  if (m < 60) return `${m}m`
  const h = Math.round(m / 60)
  if (h < 24) return `${h}h`
  return `${Math.round(h / 24)}d`
}

function Face({ author, size }: { author: CommentAuthor; size: number }) {
  return (
    <span
      className="flex flex-none items-center justify-center overflow-hidden rounded-full font-semibold text-[10px] text-white"
      style={{
        width: size,
        height: size,
        background: author.avatarUrl ? undefined : author.tint,
      }}
    >
      {author.avatarUrl ? (
        <img
          src={author.avatarUrl}
          alt=""
          referrerPolicy="no-referrer"
          className="size-full object-cover"
        />
      ) : (
        author.initials
      )}
    </span>
  )
}

/** How the event came, as the person would say it. */
const VERB: Record<string, string> = {
  comment_claimed: 'commented on',
  mentioned: 'named you on',
  comment_reply: 'replied on',
}

export function CommentWakeCard({
  message,
  passed,
  lookup,
  resolveTitle,
  onOpenDocument,
}: {
  message: TranscriptMessage
  /** The agent read it and chose not to answer; its note, or ''. */
  passed?: string
  lookup?: CommentLookup
  resolveTitle?: (documentId: string) => string | undefined
  onOpenDocument?: (documentId: string) => void
}) {
  const ref = message.ref ?? {}
  // A reply event names the comment it answered as `commentId`; what the
  // person wrote is `replyId`.
  const facts = lookup?.(ref.replyId ?? ref.commentId, ref.author)
  const author: CommentAuthor = facts?.author ?? {
    label: ref.author ?? 'Someone',
    kind: 'person',
    initials: (ref.author ?? '?').slice(0, 1).toUpperCase(),
    tint: '#6b7280',
  }
  const documentId = ref.documentId ?? facts?.documentId
  const title = documentId
    ? (resolveTitle?.(documentId) ?? 'a document')
    : 'a document'
  const body = facts?.body ?? ref.summary ?? ''
  return (
    <div className="flex flex-col gap-2">
      <div className="rounded-xl bg-muted px-3.5 py-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <Face author={author} size={28} />
          <span className="min-w-0 truncate text-[13px] text-muted-foreground">
            <span className="font-semibold text-foreground">
              {author.label}
            </span>{' '}
            {VERB[message.kind ?? ''] ?? 'commented on'}{' '}
            <span className="font-medium text-foreground">{title}</span>
          </span>
          <span className="ml-auto flex-none text-[12px] text-muted-foreground/70">
            {ago(facts?.createdAt)}
          </span>
        </div>
        {ref.anchorText ? (
          <div className="mt-2.5 text-[13px] text-muted-foreground italic">
            “{ref.anchorText}”
          </div>
        ) : null}
        {body ? (
          <p className="mt-1.5 whitespace-pre-wrap break-words text-[14px] text-foreground/85">
            {body}
          </p>
        ) : null}
        {documentId && onOpenDocument ? (
          <button
            type="button"
            onClick={() => onOpenDocument(documentId)}
            className="mt-2.5 text-[13px] text-blue-400 hover:text-blue-300"
          >
            Open in document
          </button>
        ) : null}
      </div>
      {passed !== undefined ? (
        <div className="px-0.5 text-[13px] text-muted-foreground">
          {passed ? `${passed.replace(/[.\s]*$/, '')}. ` : ''}Marked read.
        </div>
      ) : null}
    </div>
  )
}

/** The agent's own reply or comment: one line, naming whose and where. */
export function CommentActRow({
  card,
  lookup,
  resolveTitle,
  onOpenDocument,
}: {
  card: ThreadCard
  lookup?: CommentLookup
  resolveTitle?: (documentId: string) => string | undefined
  onOpenDocument?: (documentId: string) => void
}) {
  const parent = card.replyTo ? lookup?.(card.replyTo) : undefined
  const documentId = card.documentId ?? parent?.documentId
  const where = documentId ? resolveTitle?.(documentId) : undefined
  const open =
    documentId && onOpenDocument ? () => onOpenDocument(documentId) : undefined
  const lead =
    card.verb === 'Replied' ? (
      parent ? (
        <>
          Replied to{' '}
          <span className="font-medium text-foreground">
            {parent.author.label}
          </span>
        </>
      ) : (
        'Replied'
      )
    ) : (
      <>
        Commented on{' '}
        <span className="font-medium text-foreground">
          {where ?? 'a document'}
        </span>
      </>
    )
  const content = (
    <>
      <MessageSquare size={16} className="flex-none text-muted-foreground" />
      <span className="min-w-0 truncate text-[13.5px] text-foreground/85">
        {lead}
        {card.detail ? `: ${card.detail}` : ''}
      </span>
      {where && card.verb === 'Replied' ? (
        <span className="ml-auto flex-none whitespace-nowrap text-[12px] text-muted-foreground/70">
          {where}
        </span>
      ) : null}
    </>
  )
  const className = `flex w-full min-w-0 items-center gap-2.5 rounded-[10px] bg-muted/60 px-3 py-2 text-left ${
    card.pending ? 'opacity-60' : ''
  }`
  return open ? (
    <button
      type="button"
      onClick={open}
      className={`${className} hover:bg-muted`}
    >
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  )
}
